import { buildMasterVariantDisplayName } from '../../helpers/buildMasterVariantDisplayName';
import SYNC_FLOW_MEDIA_STEPS from '../constants/syncFlowMediaSteps';
import { showPublishingScopeSetupDialog } from '../PublishingScopeSetupDialog';
import { showProductMediaSelectionDialog } from '../ProductMediaSelectionDialog';
import { showVariantMediaAssignmentDialog } from '../VariantMediaAssignmentDialog';
import { showMediaAltConflictDialog } from '../MediaAltConflictDialog';
import {
  CHANNEL_SYNC_ACTION,
  createInitialFlowState,
  filterMediaStepsByAvailableAssets,
  getMediaSteps,
  MEDIA_SYNC_MODE,
  mergeFlowState,
  shouldSkipMediaSteps,
  SYNC_SETUP_MODE,
} from './syncFlowOrchestrator';
import {
  buildInitialProductMediaByChannel,
  buildInitialVariantMediaByChannel,
  getAttachedAssetsForVariant,
  getProductMediaAssets,
  getSelectedVariantIdsForSync,
  retainMediaByChannelForUnchangedActions,
} from './syncFlowMediaHelpers';
import { collectSyncAltConflictsByChannel } from './collectSyncAltConflicts';
import buildMediaAltTextByChannel from './buildMediaAltTextByChannel';

const needsUnmappedGuard = (flowState) => Boolean(flowState.publishingScope?.text);

const buildValidateBeforeFinish = ({ flowState, validateUnmappedFields }) => {
  if (!needsUnmappedGuard(flowState) || !validateUnmappedFields) {
    return undefined;
  }

  return (onUnmappedDialogOpen) => validateUnmappedFields({
    channelIds: flowState.selectedChannels,
    onUnmappedDialogOpen,
  });
};

const buildVariantOptions = (product, masterVariantIds, productsState) => {
  const variantIds = getSelectedVariantIdsForSync(product, masterVariantIds);

  return variantIds
    .map((variantId) => {
      const variant = product?.variants?.find(({ _id }) => _id === variantId);
      const attachedAssets = getAttachedAssetsForVariant(product, variantId, productsState);

      return {
        id: variantId,
        label: buildMasterVariantDisplayName({
          productTitle: product?.title || '',
          availableOptions: product?.availableOptions || [],
          variantOptions: variant?.options || {},
        }) || variant?.title || variantId,
        attachedAssets,
      };
    })
    .filter(({ attachedAssets }) => attachedAssets.length > 0);
};

const buildInitialSetupState = (flowState) => {
  if (!flowState?.selectedChannels?.length) {
    return undefined;
  }

  return {
    selectedChannels: flowState.selectedChannels,
    channelActionsByChannelId: flowState.channelActionsByChannelId,
    publishingScope: flowState.publishingScope,
    overrideExisting: flowState.overrideExisting,
  };
};

const mergeMediaFlowState = ({
  flowState,
  product,
  productsState,
  productListings,
}) => {
  const prefillListingMedia = flowState.publishingScope?.media !== MEDIA_SYNC_MODE.REPLACE;

  return mergeFlowState(flowState, {
    productMediaByChannel: buildInitialProductMediaByChannel({
      selectedChannels: flowState.selectedChannels,
      product,
      productsState,
      productListings,
      channelActionsByChannelId: flowState.channelActionsByChannelId,
      existingProductMediaByChannel: flowState.productMediaByChannel,
      prefillListingMedia,
    }),
    variantMediaByChannel: buildInitialVariantMediaByChannel({
      selectedChannels: flowState.selectedChannels,
      product,
      productsState,
      productListings,
      masterVariantIds: flowState.masterVariantIds,
      channelActionsByChannelId: flowState.channelActionsByChannelId,
      existingVariantMediaByChannel: flowState.variantMediaByChannel,
      prefillListingMedia,
    }),
  });
};

const proceedWithAltResolutions = ({
  flowState,
  product,
  productsState,
  channelOptions,
  stepIndex,
  mediaSteps,
  onCancel,
  onComplete,
  validateUnmappedFields,
  onValidationAbort,
  openSetupDialog,
  userResolutionsByChannel = {},
}) => {
  const mediaAltTextByChannel = buildMediaAltTextByChannel({
    selectedChannels: flowState.selectedChannels,
    product,
    productsState,
    productMediaByChannel: flowState.productMediaByChannel,
    variantMediaByChannel: flowState.variantMediaByChannel,
    masterVariantIds: flowState.masterVariantIds,
    isVariantSync: flowState.isVariantSync,
    userResolutionsByChannel,
  });

  runMediaSteps({
    flowState: mergeFlowState(flowState, { mediaAltTextByChannel }),
    product,
    productsState,
    channelOptions,
    stepIndex: stepIndex + 1,
    mediaSteps,
    onCancel,
    onComplete,
    validateUnmappedFields,
    onValidationAbort,
    openSetupDialog,
  });
};

const runMediaSteps = ({
  flowState,
  product,
  productsState,
  channelOptions,
  stepIndex,
  mediaSteps,
  onCancel,
  onComplete,
  validateUnmappedFields,
  onValidationAbort,
  openSetupDialog,
}) => {
  const currentStep = mediaSteps[stepIndex];

  if (!currentStep) {
    onComplete(flowState);
    return;
  }

  const isLastStep = stepIndex === mediaSteps.length - 1;
  const validateBeforeFinish = isLastStep
    ? buildValidateBeforeFinish({ flowState, validateUnmappedFields })
    : undefined;
  const previousStep = mediaSteps[stepIndex - 1];

  if (currentStep === SYNC_FLOW_MEDIA_STEPS.PRODUCT_MEDIA) {
    showProductMediaSelectionDialog({
      channelOptions,
      selectedChannels: flowState.selectedChannels,
      channelActionsByChannelId: flowState.channelActionsByChannelId,
      assets: getProductMediaAssets(product, productsState),
      productMediaByChannel: flowState.productMediaByChannel,
      lockedAssetIdsByChannel: flowState.lockedProductMediaByChannel,
      validateBeforeFinish,
      onCancel,
      onValidationAbort,
      onBack: (productMediaByChannel) => {
        openSetupDialog(mergeFlowState(flowState, { productMediaByChannel }));
      },
      onConfirm: (productMediaByChannel) => {
        runMediaSteps({
          flowState: mergeFlowState(flowState, { productMediaByChannel }),
          product,
          productsState,
          channelOptions,
          stepIndex: stepIndex + 1,
          mediaSteps,
          onCancel,
          onComplete,
          validateUnmappedFields,
          onValidationAbort,
          openSetupDialog,
        });
      },
    });
    return;
  }

  if (currentStep === SYNC_FLOW_MEDIA_STEPS.VARIANT_MEDIA) {
    showVariantMediaAssignmentDialog({
      channelOptions,
      selectedChannels: flowState.selectedChannels,
      channelActionsByChannelId: flowState.channelActionsByChannelId,
      variantOptions: buildVariantOptions(product, flowState.masterVariantIds, productsState),
      variantMediaByChannel: flowState.variantMediaByChannel,
      validateBeforeFinish,
      onCancel,
      onValidationAbort,
      onBack: (variantMediaByChannel) => {
        const nextFlowState = mergeFlowState(flowState, { variantMediaByChannel });

        if (previousStep === SYNC_FLOW_MEDIA_STEPS.PRODUCT_MEDIA) {
          runMediaSteps({
            flowState: nextFlowState,
            product,
            productsState,
            channelOptions,
            stepIndex: stepIndex - 1,
            mediaSteps,
            onCancel,
            onComplete,
            validateUnmappedFields,
            onValidationAbort,
            openSetupDialog,
          });
          return;
        }

        openSetupDialog(nextFlowState);
      },
      onConfirm: (variantMediaByChannel) => {
        runMediaSteps({
          flowState: mergeFlowState(flowState, {
            variantMediaByChannel,
            variantMediaStepCompleted: true,
          }),
          product,
          productsState,
          channelOptions,
          stepIndex: stepIndex + 1,
          mediaSteps,
          onCancel,
          onComplete,
          validateUnmappedFields,
          onValidationAbort,
          openSetupDialog,
        });
      },
    });
    return;
  }

  if (currentStep === SYNC_FLOW_MEDIA_STEPS.ALT_CONFLICTS) {
    const conflictsByChannel = collectSyncAltConflictsByChannel({
      selectedChannels: flowState.selectedChannels,
      product,
      productsState,
      productMediaByChannel: flowState.productMediaByChannel,
      variantMediaByChannel: flowState.variantMediaByChannel,
      masterVariantIds: flowState.masterVariantIds,
      isVariantSync: flowState.isVariantSync,
    });

    const hasConflicts = Object.keys(conflictsByChannel).length > 0;

    if (!hasConflicts) {
      proceedWithAltResolutions({
        flowState,
        product,
        productsState,
        channelOptions,
        stepIndex,
        mediaSteps,
        onCancel,
        onComplete,
        validateUnmappedFields,
        onValidationAbort,
        openSetupDialog,
      });
      return;
    }

    showMediaAltConflictDialog({
      channelOptions,
      selectedChannels: flowState.selectedChannels,
      conflictsByChannel,
      validateBeforeFinish,
      onCancel,
      onValidationAbort,
      onBack: () => {
        const previousStep = mediaSteps[stepIndex - 1];

        if (previousStep === SYNC_FLOW_MEDIA_STEPS.VARIANT_MEDIA) {
          runMediaSteps({
            flowState,
            product,
            productsState,
            channelOptions,
            stepIndex: stepIndex - 1,
            mediaSteps,
            onCancel,
            onComplete,
            validateUnmappedFields,
            onValidationAbort,
            openSetupDialog,
          });
          return;
        }

        if (previousStep === SYNC_FLOW_MEDIA_STEPS.PRODUCT_MEDIA) {
          runMediaSteps({
            flowState,
            product,
            productsState,
            channelOptions,
            stepIndex: stepIndex - 1,
            mediaSteps,
            onCancel,
            onComplete,
            validateUnmappedFields,
            onValidationAbort,
            openSetupDialog,
          });
        }
      },
      onConfirm: (userResolutionsByChannel) => {
        proceedWithAltResolutions({
          flowState,
          product,
          productsState,
          channelOptions,
          stepIndex,
          mediaSteps,
          onCancel,
          onComplete,
          validateUnmappedFields,
          onValidationAbort,
          openSetupDialog,
          userResolutionsByChannel,
        });
      },
    });
  }
};

const continueAfterScopeSetup = async ({
  flowState,
  product,
  productsState,
  productListings,
  channelOptions,
  onCancel,
  onComplete,
  validateUnmappedFields,
  onValidationAbort,
  openSetupDialog,
  waitForSyncMedia,
  getProductsState,
}) => {
  if (shouldSkipMediaSteps(flowState.publishingScope)) {
    onComplete(flowState);
    return;
  }

  if (waitForSyncMedia) {
    try {
      await waitForSyncMedia(flowState);
    } catch (error) {
      onCancel?.();
      return;
    }
  }

  const currentProductsState = getProductsState?.() ?? productsState;

  const mediaSteps = filterMediaStepsByAvailableAssets({
    steps: getMediaSteps({
      product,
      isVariantSync: flowState.isVariantSync,
      scope: flowState.publishingScope,
    }),
    product,
    masterVariantIds: flowState.masterVariantIds,
    productsState: currentProductsState,
    isVariantSync: flowState.isVariantSync,
  });

  const mediaFlowState = mergeMediaFlowState({
    flowState,
    product,
    productsState: currentProductsState,
    productListings,
  });
  const lockedProductMediaByChannel = flowState.publishingScope?.media === MEDIA_SYNC_MODE.ADD
    ? buildInitialProductMediaByChannel({
      selectedChannels: mediaFlowState.selectedChannels,
      product,
      productsState: currentProductsState,
      productListings,
      channelActionsByChannelId: mediaFlowState.channelActionsByChannelId,
      existingProductMediaByChannel: {},
    })
    : undefined;

  runMediaSteps({
    flowState: mergeFlowState(mediaFlowState, {
      lockedProductMediaByChannel,
    }),
    product,
    productsState: currentProductsState,
    channelOptions,
    stepIndex: 0,
    mediaSteps,
    onCancel,
    onComplete,
    validateUnmappedFields,
    onValidationAbort,
    openSetupDialog,
  });
};

export const startSyncFlow = ({
  product,
  productId,
  productsState,
  productListings,
  channelOptions,
  masterVariantIds,
  isVariantSync = false,
  syncAction = CHANNEL_SYNC_ACTION.DRAFT,
  shouldShowVariantNote,
  onCancel,
  onComplete,
  validateUnmappedFields,
  waitForSyncMedia,
  getProductsState,
}) => {
  const flowState = createInitialFlowState({
    productId,
    masterVariantIds,
    isVariantSync,
  });

  const openSetupDialog = (flowStateOverride = null) => {
    const currentFlowState = flowStateOverride ?? flowState;

    showPublishingScopeSetupDialog({
      mode: syncAction === CHANNEL_SYNC_ACTION.PUBLISH
        ? SYNC_SETUP_MODE.PUBLISH
        : SYNC_SETUP_MODE.CREATE_DRAFTS,
      channelOptions,
      productListings,
      product,
      productsState,
      isVariantSync,
      masterVariantIds,
      shouldShowVariantNote,
      initialSetupState: buildInitialSetupState(currentFlowState),
      onCancel,
      onValidationAbort: () => openSetupDialog(currentFlowState),
      onProceed: async ({
        selectedChannels,
        channelActionsByChannelId,
        publishingScope,
        overrideExisting,
      }, { onUnmappedDialogOpen } = {}) => {
        const activeSelectedChannels = selectedChannels.filter((channelId) => {
          const option = channelOptions.find(({ value }) => value === channelId);
          return option && !option.disconnected;
        });

        if (!activeSelectedChannels.length) {
          return;
        }

        const activeChannelActions = activeSelectedChannels.reduce((acc, channelId) => ({
          ...acc,
          [channelId]: channelActionsByChannelId[channelId],
        }), {});

        const previousChannelActions = currentFlowState.channelActionsByChannelId || {};
        const mediaModeUnchanged = (
          currentFlowState.publishingScope?.media === publishingScope.media
        );
        const retainedProductMediaByChannel = mediaModeUnchanged
          ? retainMediaByChannelForUnchangedActions({
            mediaByChannel: currentFlowState.productMediaByChannel,
            previousActionsByChannelId: previousChannelActions,
            nextActionsByChannelId: activeChannelActions,
            selectedChannels: activeSelectedChannels,
          })
          : {};
        const retainedVariantMediaByChannel = mediaModeUnchanged
          ? retainMediaByChannelForUnchangedActions({
            mediaByChannel: currentFlowState.variantMediaByChannel,
            previousActionsByChannelId: previousChannelActions,
            nextActionsByChannelId: activeChannelActions,
            selectedChannels: activeSelectedChannels,
          })
          : {};

        const nextFlowState = mergeFlowState(currentFlowState, {
          selectedChannels: activeSelectedChannels,
          channelActionsByChannelId: activeChannelActions,
          publishingScope,
          overrideExisting,
          productMediaByChannel: retainedProductMediaByChannel,
          variantMediaByChannel: retainedVariantMediaByChannel,
        });

        if (
          needsUnmappedGuard(nextFlowState)
          && shouldSkipMediaSteps(nextFlowState.publishingScope)
          && validateUnmappedFields
        ) {
          await validateUnmappedFields({
            channelIds: nextFlowState.selectedChannels,
            onUnmappedDialogOpen,
          });
        }

        await continueAfterScopeSetup({
          flowState: nextFlowState,
          product,
          productsState,
          productListings,
          channelOptions,
          onCancel,
          onComplete,
          validateUnmappedFields,
          onValidationAbort: () => openSetupDialog(nextFlowState),
          openSetupDialog,
          waitForSyncMedia,
          getProductsState,
        });
      },
    });
  };

  openSetupDialog();
};

export default startSyncFlow;
